import React, { useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'

import useText from '../../../../common/hooks/useText'
import { Dialog } from '../../../../common/uikit/Dialog'

export interface CapiLogFilters {
    [key: string]: Record<string, boolean> | boolean
}

export type CapiLogsFilterDialogProps = {
    visible: boolean
    onHide: () => void
    onApply: (newFilters: CapiLogFilters) => void
    filters: CapiLogFilters
}

const filterTitleKeys: Record<string, string> = {
    logLevel: 'lblLogLevel',
    debug: 'lblDebugField',
    info: 'lblInfoField',
    warn: 'lblWarningField',
    error: 'lblErrorField',
    origin: 'lblOrigin',
    lb: 'lblLoadbalancerShortField',
    ocpp: 'lblOcpp',
    capi: 'lblCapiField',
    measure: 'lblMeasureField',
    batch: 'lblBatchField',
    device: 'lblDeviceField',
    web: 'lblWebField',
    WifiInfo: 'lblWifiInfo',
    Temperature: 'lblTemperature',
    ProcessorVoltage: 'lblProcessorVoltage',
    PwmStateShifts: 'lblPWMStateShifts',
    Pwm: 'lblPWM',
    GfcidcWaveform: 'lblGFCIDCWaveForm',
    ClientInfo: 'lblClientInfo',
    dataType: 'lblDataType',
}

export function CapiLogsFilterDialog({
    visible,
    onHide,
    onApply,
    filters,
}: CapiLogsFilterDialogProps): React.ReactElement {
    const text = useText()
    const [selectedFilters, setSelectedFilters] = useState<CapiLogFilters>(() => ({ ...filters }))

    useEffect(() => {
        if (visible) {
            setSelectedFilters({ ...filters })
        }
    }, [filters, visible])

    const getFilterTitle = (filter: string) => {
        const translationKey = filterTitleKeys[filter]

        return translationKey ? text(translationKey) : filter
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} text onClick={onHide} type="button" />
            <Button
                label={text('btnApply')}
                onClick={() => {
                    onApply(selectedFilters)
                    onHide()
                }}
                type="button"
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblFilterModal')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="capi-logs-filter-dialog"
        >
            <div className="flex flex-column gap-3 text-base font-normal">
                {Object.entries(selectedFilters).map(([filterGroup, filterGroupValue], groupIndex) => (
                    <section key={filterGroup} className="flex flex-column gap-2">
                        <h3
                            className="text-base font-semibold line-height-2 m-0"
                            data-testid={`filter-group-${groupIndex}`}
                        >
                            {getFilterTitle(filterGroup)}
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            {typeof filterGroupValue === 'object' ? (
                                Object.entries(filterGroupValue).map(([filter, checked], filterIndex) => {
                                    const inputId = `filter-${filterGroup}-${filter}`

                                    return (
                                        <div
                                            key={filter}
                                            className="flex align-items-center gap-2"
                                            data-testid={`filter-name-${filterIndex}`}
                                        >
                                            <Checkbox
                                                inputId={inputId}
                                                checked={checked}
                                                onChange={(event) =>
                                                    setSelectedFilters((currentFilters) => ({
                                                        ...currentFilters,
                                                        [filterGroup]: {
                                                            ...(currentFilters[filterGroup] as Record<string, boolean>),
                                                            [filter]: event.checked === true,
                                                        },
                                                    }))
                                                }
                                                pt={{ input: { 'data-testid': `filter-${filterIndex}` } }}
                                            />
                                            <label
                                                htmlFor={inputId}
                                                className="text-base font-normal line-height-2 mb-0 cursor-pointer"
                                            >
                                                {getFilterTitle(filter)}
                                            </label>
                                        </div>
                                    )
                                })
                            ) : (
                                <div
                                    className="flex align-items-center gap-2"
                                    data-testid={`filter-name-one-${groupIndex}`}
                                >
                                    <Checkbox
                                        inputId={`filter-${filterGroup}`}
                                        checked={filterGroupValue}
                                        onChange={(event) =>
                                            setSelectedFilters((currentFilters) => ({
                                                ...currentFilters,
                                                [filterGroup]: event.checked === true,
                                            }))
                                        }
                                        pt={{ input: { 'data-testid': `filter-one-${groupIndex}` } }}
                                    />
                                    <label
                                        htmlFor={`filter-${filterGroup}`}
                                        className="text-base font-normal line-height-2 mb-0 cursor-pointer"
                                    >
                                        {getFilterTitle(filterGroup)}
                                    </label>
                                </div>
                            )}
                        </div>
                    </section>
                ))}
            </div>
        </Dialog>
    )
}
